import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Card from 'components/card';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import {
    MdArrowBack,
    MdCheck,
    MdClose,
    MdDelete,
    MdEdit,
    MdPersonAdd,
    MdPersonRemove,
    MdSearch,
    MdSend,
} from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import TemplatesFallbackNotice from './TemplatesFallbackNotice';
import {
    Industry,
    TemplatesFallback,
    UserWithPhone,
    WhatsappList,
    WhatsappTemplate,
    extractPlaceholders,
    getAvailableSubIndustries,
} from './types';

const TypeBadge = ({ type }: { type: string }) => (
    <span
        className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            type === 'buyer' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
        }`}
    >
        {type}
    </span>
);

export default function BackOfficeWhatsappListDetail() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [list, setList] = useState<WhatsappList | null>(null);
    const [members, setMembers] = useState<UserWithPhone[]>([]);
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [templates, setTemplates] = useState<WhatsappTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [templatesFallback, setTemplatesFallback] = useState<TemplatesFallback>({ fallback: false });

    // Edit list
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editIndustries, setEditIndustries] = useState<string[]>([]);
    const [editSubIndustries, setEditSubIndustries] = useState<string[]>([]);
    const [savingEdit, setSavingEdit] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(false);

    // Members
    const [memberFilter, setMemberFilter] = useState('');
    const [removingUserId, setRemovingUserId] = useState<string | null>(null);

    // Add members
    const [showAddPanel, setShowAddPanel] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<UserWithPhone[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [addingUserId, setAddingUserId] = useState<string | null>(null);

    // Send
    const [selectedSid, setSelectedSid] = useState<string | null>(null);
    const [variableValues, setVariableValues] = useState<Record<string, string>>({});
    const [sending, setSending] = useState(false);
    const [sendResult, setSendResult] = useState<{ sent: number; failed: number; total: number } | null>(null);

    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        const load = async () => {
            try {
                setLoading(true);
                const [listRes, usersRes] = await Promise.all([
                    axios.get(`${CONFIG.apiUrl}/admin/whatsapp-lists/${id}`, { headers }),
                    axios.get(`${CONFIG.apiUrl}/admin/whatsapp-lists/${id}/users`, { headers }),
                ]);
                setList(listRes.data.list);
                setMembers(usersRes.data.users);
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        };
        const loadIndustries = async () => {
            try {
                const res = await axios.get(`${CONFIG.apiUrl}/admin/industries-list`, { headers });
                setIndustries(res.data.industries);
            } catch (e) {
                console.error(e);
            }
        };
        const loadTemplates = async () => {
            try {
                setLoadingTemplates(true);
                const res = await axios.get(`${CONFIG.apiUrl}/admin/whatsapp-templates`, { headers });
                setTemplates(res.data.templates);
                setTemplatesFallback({ fallback: !!res.data.fallback, fallbackReason: res.data.fallbackReason });
            } catch (e) {
                console.error(e);
            } finally {
                setLoadingTemplates(false);
            }
        };
        load();
        loadIndustries();
        loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // ─── Edit / delete list ───────────────────────────────────────────────────

    const startEdit = () => {
        if (!list) return;
        setEditName(list.name);
        setEditDescription(list.description);
        setEditIndustries(list.industries.map((i) => i._id));
        setEditSubIndustries(list.subIndustries);
        setEditing(true);
    };

    const handleSaveEdit = async () => {
        if (!list || !editName.trim()) return;
        try {
            setSavingEdit(true);
            const res = await axios.put(`${CONFIG.apiUrl}/admin/whatsapp-lists/${list._id}`, {
                name: editName.trim(),
                description: editDescription.trim(),
                industries: editIndustries,
                subIndustries: editSubIndustries,
            }, { headers });
            setList(res.data.list);
            setEditing(false);
        } catch (e) {
            console.error(e);
            alert('Failed to save changes');
        } finally {
            setSavingEdit(false);
        }
    };

    const handleDeleteList = async () => {
        if (!list) return;
        try {
            await axios.delete(`${CONFIG.apiUrl}/admin/whatsapp-lists/${list._id}`, { headers });
            navigate('/back-office/whatsapp-diffusion');
        } catch (e) {
            console.error(e);
            alert('Failed to delete list');
        }
    };

    const toggle = (setter: React.Dispatch<React.SetStateAction<string[]>>, value: string) =>
        setter((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

    // ─── Members ──────────────────────────────────────────────────────────────

    const filteredMembers = useMemo(() => {
        const term = memberFilter.trim().toLowerCase();
        if (!term) return members;
        return members.filter((m) =>
            [m.name, m.surname, m.email, m.phone].some((v) => v?.toLowerCase().includes(term))
        );
    }, [members, memberFilter]);

    const searchUsers = useCallback(async (term: string) => {
        try {
            setSearchLoading(true);
            const res = await axios.get(`${CONFIG.apiUrl}/admin/users-with-phone`, {
                headers,
                params: { search: term, limit: 30 },
            });
            setSearchResults(res.data.users);
        } catch (e) {
            console.error(e);
        } finally {
            setSearchLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!showAddPanel) return;
        const timeout = setTimeout(() => searchUsers(searchTerm), 300);
        return () => clearTimeout(timeout);
    }, [searchTerm, showAddPanel, searchUsers]);

    const isMember = (userId: string) => members.some((m) => m._id === userId);

    const handleAddUser = async (user: UserWithPhone) => {
        if (!list) return;
        try {
            setAddingUserId(user._id);
            await axios.post(`${CONFIG.apiUrl}/admin/whatsapp-lists/${list._id}/users/${user._id}`, {}, { headers });
            setMembers((prev) => (prev.some((m) => m._id === user._id) ? prev : [user, ...prev]));
        } catch (e) {
            console.error(e);
            alert('Failed to add user');
        } finally {
            setAddingUserId(null);
        }
    };

    const handleRemoveUser = async (userId: string) => {
        if (!list) return;
        try {
            setRemovingUserId(userId);
            await axios.delete(`${CONFIG.apiUrl}/admin/whatsapp-lists/${list._id}/users/${userId}`, { headers });
            setMembers((prev) => prev.filter((m) => m._id !== userId));
        } catch (e) {
            console.error(e);
            alert('Failed to remove user');
        } finally {
            setRemovingUserId(null);
        }
    };

    // ─── Send ─────────────────────────────────────────────────────────────────

    const approvedTemplates = useMemo(
        () => templates.filter((t) => t.approval?.status === 'approved'),
        [templates]
    );
    const selectedTemplate = approvedTemplates.find((t) => t.sid === selectedSid) || null;
    const placeholders = selectedTemplate ? extractPlaceholders(selectedTemplate.body) : [];
    const recipients = members.filter((m) => m.phone).length;

    const selectTemplate = (tpl: WhatsappTemplate) => {
        setSelectedSid(tpl.sid);
        setSendResult(null);
        const initial: Record<string, string> = {};
        extractPlaceholders(tpl.body).forEach((k) => { initial[k] = tpl.variables?.[k] ?? ''; });
        setVariableValues(initial);
    };

    const renderPreview = (body: string) =>
        body.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => variableValues[k] || `{{${k}}}`);

    const handleSend = async () => {
        if (!list || !selectedTemplate) return;
        if (recipients === 0) {
            alert('Aucun membre avec numéro de téléphone dans cette liste.');
            return;
        }
        if (placeholders.some((k) => !variableValues[k]?.trim())) {
            alert('Renseignez toutes les variables du template.');
            return;
        }
        if (!window.confirm(`Envoyer le template "${selectedTemplate.friendlyName}" à ${recipients} membre(s) ?`)) return;
        try {
            setSending(true);
            setSendResult(null);
            const res = await axios.post(
                `${CONFIG.apiUrl}/admin/whatsapp-lists/${list._id}/send`,
                { contentSid: selectedTemplate.sid, contentVariables: variableValues },
                { headers }
            );
            setSendResult(res.data);
        } catch (e: any) {
            console.error(e);
            alert(e?.response?.data?.message || 'Échec de l\'envoi');
        } finally {
            setSending(false);
        }
    };

    // ─── Render ───────────────────────────────────────────────────────────────

    if (loading) {
        return <div className="pt-[50px] py-8 text-center text-sm text-gray-400">Chargement...</div>;
    }

    if (!list) {
        return (
            <div className="pt-[50px]">
                <Card extra="p-8 text-center text-gray-400">
                    <p className="text-sm">Liste introuvable</p>
                    <button
                        onClick={() => navigate('/back-office/whatsapp-diffusion')}
                        className="mt-3 text-sm text-brand-500 hover:underline"
                    >
                        Retour aux listes
                    </button>
                </Card>
            </div>
        );
    }

    const chipClass = (active: boolean) =>
        `rounded px-2 py-0.5 text-xs ${
            active ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 dark:bg-navy-600 dark:text-gray-300'
        }`;

    return (
        <div className="pt-[50px]">
            <button
                onClick={() => navigate('/back-office/whatsapp-diffusion')}
                className="mb-3 flex items-center gap-1 text-sm text-gray-500 hover:text-navy-700 dark:text-gray-400 dark:hover:text-white"
            >
                <MdArrowBack /> Listes diffusion
            </button>

            {/* Header */}
            <Card extra="p-4 mb-4">
                {editing ? (
                    <div className="space-y-3">
                        <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            placeholder="Nom de la liste"
                        />
                        <textarea
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            rows={2}
                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                            placeholder="Description"
                        />
                        {industries.length > 0 && (
                            <div>
                                <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Industries</div>
                                <div className="flex flex-wrap gap-1">
                                    {industries.map((ind) => (
                                        <button
                                            key={ind._id}
                                            onClick={() => toggle(setEditIndustries, ind._id)}
                                            className={chipClass(editIndustries.includes(ind._id))}
                                        >
                                            {ind.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        {editIndustries.length > 0 && (
                            <div>
                                <div className="mb-1 text-xs font-medium text-gray-500 dark:text-gray-400">Sous-industries</div>
                                <div className="flex flex-wrap gap-1">
                                    {getAvailableSubIndustries(industries, editIndustries).map((sub) => (
                                        <button
                                            key={sub}
                                            onClick={() => toggle(setEditSubIndustries, sub)}
                                            className={chipClass(editSubIndustries.includes(sub))}
                                        >
                                            {sub}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button
                                onClick={handleSaveEdit}
                                disabled={savingEdit}
                                className="flex items-center gap-1 rounded-lg bg-green-500 px-3 py-1.5 text-sm text-white hover:bg-green-600 disabled:opacity-50"
                            >
                                <MdCheck /> {savingEdit ? 'Sauvegarde...' : 'Sauvegarder'}
                            </button>
                            <button
                                onClick={() => setEditing(false)}
                                className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300"
                            >
                                Annuler
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex items-center gap-2">
                                <FaWhatsapp className="text-2xl text-green-500" />
                                <h2 className="text-xl font-bold text-navy-700 dark:text-white">{list.name}</h2>
                            </div>
                            {list.description && (
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{list.description}</p>
                            )}
                            <div className="mt-2 flex flex-wrap gap-1">
                                {list.industries.map((ind) => (
                                    <span
                                        key={ind._id}
                                        className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                                    >
                                        {ind.name}
                                    </span>
                                ))}
                                {list.subIndustries.map((sub) => (
                                    <span
                                        key={sub}
                                        className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                                    >
                                        {sub}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                            <button
                                onClick={startEdit}
                                className="flex items-center gap-1 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300"
                            >
                                <MdEdit /> Modifier
                            </button>
                            {deleteConfirm ? (
                                <div className="flex items-center gap-1">
                                    <span className="text-xs text-gray-500">Supprimer ?</span>
                                    <button onClick={handleDeleteList} className="rounded bg-red-500 px-2 py-1 text-xs text-white">
                                        Oui
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfirm(false)}
                                        className="rounded bg-gray-400 px-2 py-1 text-xs text-white"
                                    >
                                        Non
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setDeleteConfirm(true)}
                                    className="flex items-center gap-1 rounded-lg border border-red-200 px-3 py-1.5 text-sm text-red-500 hover:bg-red-50 dark:border-red-900/40 dark:hover:bg-red-900/20"
                                >
                                    <MdDelete /> Supprimer
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </Card>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
                {/* Members */}
                <div className="space-y-4 xl:col-span-3">
                    <Card extra="p-4">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <div className="text-base font-semibold text-navy-700 dark:text-white">
                                Membres ({members.length})
                            </div>
                            <div className="flex items-center gap-2">
                                <div className="relative">
                                    <MdSearch className="pointer-events-none absolute left-2.5 top-2 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Filtrer..."
                                        value={memberFilter}
                                        onChange={(e) => setMemberFilter(e.target.value)}
                                        className="w-44 rounded-lg border border-gray-300 bg-white py-1.5 pl-8 pr-3 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                    />
                                </div>
                                <button
                                    onClick={() => {
                                        setShowAddPanel(!showAddPanel);
                                        setSearchTerm('');
                                    }}
                                    className="flex items-center gap-1 rounded-lg bg-brand-500 px-3 py-1.5 text-sm text-white hover:bg-brand-600"
                                >
                                    <MdPersonAdd /> Ajouter
                                </button>
                            </div>
                        </div>

                        {showAddPanel && (
                            <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 dark:border-navy-600 dark:bg-navy-800">
                                <div className="mb-2 flex items-center justify-between">
                                    <div className="text-sm font-semibold text-navy-700 dark:text-white">Ajouter des membres</div>
                                    <button
                                        onClick={() => setShowAddPanel(false)}
                                        className="rounded p-1 text-gray-400 hover:text-gray-600"
                                    >
                                        <MdClose />
                                    </button>
                                </div>
                                <div className="relative mb-2">
                                    <MdSearch className="pointer-events-none absolute left-3 top-2.5 text-gray-400" />
                                    <input
                                        type="text"
                                        autoFocus
                                        placeholder="Rechercher par nom, email ou téléphone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm focus:border-brand-500 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                    />
                                </div>
                                {searchLoading ? (
                                    <div className="py-4 text-center text-sm text-gray-400">Recherche...</div>
                                ) : searchResults.length === 0 ? (
                                    <div className="py-4 text-center text-sm text-gray-400">
                                        Aucun utilisateur avec numéro de téléphone
                                    </div>
                                ) : (
                                    <div className="max-h-72 divide-y divide-gray-100 overflow-y-auto dark:divide-navy-700">
                                        {searchResults.map((user) => {
                                            const inList = isMember(user._id);
                                            return (
                                                <div key={user._id} className="flex items-center gap-3 py-2">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="truncate text-sm font-medium text-navy-700 dark:text-white">
                                                            {user.name} {user.surname}
                                                        </div>
                                                        <div className="truncate text-xs text-gray-400">
                                                            {user.email} · <span className="font-mono">{user.phone}</span>
                                                        </div>
                                                    </div>
                                                    <TypeBadge type={user.type} />
                                                    {inList ? (
                                                        <button
                                                            onClick={() => handleRemoveUser(user._id)}
                                                            disabled={removingUserId === user._id}
                                                            className="flex w-24 items-center justify-center gap-1 rounded border border-red-200 px-2 py-1 text-xs text-red-500 hover:bg-red-50 disabled:opacity-50 dark:border-red-900/40 dark:hover:bg-red-900/20"
                                                        >
                                                            <MdPersonRemove /> Retirer
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleAddUser(user)}
                                                            disabled={addingUserId === user._id}
                                                            className="flex w-24 items-center justify-center gap-1 rounded bg-brand-500 px-2 py-1 text-xs text-white hover:bg-brand-600 disabled:opacity-50"
                                                        >
                                                            <MdPersonAdd /> Ajouter
                                                        </button>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {members.length === 0 ? (
                            <div className="py-6 text-center text-sm text-gray-400">Aucun membre dans cette liste</div>
                        ) : filteredMembers.length === 0 ? (
                            <div className="py-6 text-center text-sm text-gray-400">Aucun membre ne correspond</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-gray-200 dark:border-navy-600">
                                            {['Nom', 'Email', 'Téléphone', 'Type'].map((h) => (
                                                <th key={h} className="pb-2 text-left text-xs font-bold uppercase tracking-wide text-gray-500">
                                                    {h}
                                                </th>
                                            ))}
                                            <th className="pb-2" />
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredMembers.map((user) => (
                                            <tr key={user._id} className="border-b border-gray-100 dark:border-navy-700">
                                                <td className="py-2 text-sm text-navy-700 dark:text-white">
                                                    {user.name} {user.surname}
                                                </td>
                                                <td className="py-2 text-sm text-gray-500">{user.email}</td>
                                                <td className="py-2 font-mono text-sm text-gray-600 dark:text-gray-300">{user.phone}</td>
                                                <td className="py-2"><TypeBadge type={user.type} /></td>
                                                <td className="py-2 text-right">
                                                    <button
                                                        onClick={() => handleRemoveUser(user._id)}
                                                        disabled={removingUserId === user._id}
                                                        className="ml-auto flex items-center gap-1 rounded px-2 py-1 text-xs text-red-500 hover:bg-red-50 disabled:opacity-50 dark:hover:bg-red-900/20"
                                                    >
                                                        <MdPersonRemove /> Retirer
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Card>
                </div>

                {/* Send */}
                <div className="xl:col-span-2">
                    <Card extra="p-4">
                        <div className="mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-base font-semibold text-navy-700 dark:text-white">
                                <FaWhatsapp className="text-green-500" />
                                Envoyer un message
                            </div>
                            <button
                                onClick={() => navigate('/back-office/whatsapp-templates')}
                                className="text-xs text-brand-500 hover:underline"
                            >
                                Gérer les templates
                            </button>
                        </div>

                        {templatesFallback.fallback && (
                            <TemplatesFallbackNotice reason={templatesFallback.fallbackReason} className="mb-3" />
                        )}

                        {loadingTemplates ? (
                            <div className="py-4 text-center text-sm text-gray-400">Chargement des templates...</div>
                        ) : approvedTemplates.length === 0 ? (
                            <div className="py-4 text-sm text-gray-400">Aucun template approuvé par WhatsApp.</div>
                        ) : (
                            <div className="mb-4 max-h-80 space-y-2 overflow-y-auto">
                                {approvedTemplates.map((tpl) => (
                                    <label
                                        key={tpl.sid}
                                        className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                                            selectedSid === tpl.sid
                                                ? 'border-green-400 bg-green-50 dark:border-green-600 dark:bg-green-900/20'
                                                : 'border-gray-200 hover:border-gray-300 dark:border-navy-600 dark:hover:border-navy-500'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="whatsapp-template"
                                            checked={selectedSid === tpl.sid}
                                            onChange={() => selectTemplate(tpl)}
                                            className="mt-0.5 accent-green-500"
                                        />
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="truncate text-sm font-medium text-navy-700 dark:text-white">
                                                    {tpl.friendlyName}
                                                </span>
                                                {tpl.approval?.category && (
                                                    <span className="text-[10px] uppercase tracking-wide text-gray-400">
                                                        {tpl.approval.category}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="mt-1 line-clamp-2 text-xs text-gray-500 dark:text-gray-400">{tpl.body}</div>
                                        </div>
                                    </label>
                                ))}
                            </div>
                        )}

                        {selectedTemplate && placeholders.length > 0 && (
                            <div className="mb-4 space-y-2">
                                <div className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                    Variables — utilisez {'{prenom}'} ou {'{nom}'} pour personnaliser
                                </div>
                                {placeholders.map((k) => (
                                    <div key={k} className="flex items-center gap-2">
                                        <span className="w-12 shrink-0 font-mono text-xs text-gray-500">{`{{${k}}}`}</span>
                                        <input
                                            type="text"
                                            value={variableValues[k] ?? ''}
                                            onChange={(e) => setVariableValues((prev) => ({ ...prev, [k]: e.target.value }))}
                                            className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-sm focus:border-green-400 focus:outline-none dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                                        />
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedTemplate && (
                            <div className="mb-4 whitespace-pre-wrap rounded-lg bg-[#e7fdd8] p-3 text-sm text-gray-800 dark:bg-green-900/30 dark:text-gray-100">
                                {renderPreview(selectedTemplate.body)}
                            </div>
                        )}

                        <button
                            onClick={handleSend}
                            disabled={!selectedTemplate || sending || recipients === 0}
                            className="flex w-full items-center justify-center gap-2 rounded-lg bg-green-500 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            <MdSend />
                            {sending ? 'Envoi en cours...' : `Envoyer à ${recipients} membre(s)`}
                        </button>

                        {sendResult && (
                            <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm dark:border-green-800 dark:bg-green-900/20">
                                <div className="font-medium text-green-700 dark:text-green-300">Diffusion terminée</div>
                                <div className="mt-1 text-green-600 dark:text-green-400">
                                    ✓ {sendResult.sent} envoyé(s) &nbsp;
                                    {sendResult.failed > 0 && (
                                        <span className="text-red-500">✗ {sendResult.failed} échoué(s)</span>
                                    )}
                                </div>
                            </div>
                        )}
                    </Card>
                </div>
            </div>
        </div>
    );
}
